import React, { useContext, useEffect, useState, useRef } from "react";
import { Box, Grid } from "@mui/material";
import FeatureBox from "../../components/FeatureBox";
import FeaturedSection from "../../components/Homev2/FeaturedSection";
import BannerSection from "../../components/Homev2/BannerSection";
import SidebarRight from "../../components/Homev2/SidebarRight";
import ApiService from "./../../services/api-service";
import notifier from "./../../components/Notifier";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { startStream } from "../../action/stream";
import { SocketContext } from "../../context/socket";
import SIKIload from "../../components/SIKIload/SIKIload";
import VideoCarousel from "../../components/Homev2/VideoCarousel";
import TopBan from "../../components/Homev2/topban";
import FeaturedArtists from "../../components/Homev2/FeaturedArtists";

export default function HomePage({ studioId, onVideoSelect, isOwner }) {
  const socket = useContext(SocketContext).Socket;
  const { t } = useTranslation("common");
  const [liveStream, setLiveStream] = useState([]);
  const [bannerData, setBannerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const streamRef = useRef([]);

  useEffect(() => {
    // Initial data load
    const loadData = async () => {
      setLoading(true);
      try {
        const [liveStreamData, banner] = await Promise.all([
          fetchLiveStreams(),
          fetchBannerData()
        ]);
        setLiveStream(liveStreamData);
        streamRef.current = liveStreamData;
        setBannerData(banner);
      } catch (error) {
        console.error("Error loading data:", error);
        notifier.error("Failed to load data.");
      } finally {
        setLoading(false);
      }
    };

    loadData();

    // WebSocket listeners for live stream updates
    socket.on("stream-joined", handleStreamJoined);
    socket.on("stream-ended", handleStreamEnded);

    return () => {
      socket.off("stream-joined", handleStreamJoined);
      socket.off("stream-ended", handleStreamEnded);
    };
  }, [socket]);

  const fetchLiveStreams = async () => {
    try {
      const response = await ApiService.LIVESTREAM();
      return response.result || [];
    } catch (error) {
      notifier.error("Error fetching live streams.");
      return [];
    }
  };

  const fetchBannerData = async () => {
    try {
      const [songResponse, playlistResponse] = await Promise.all([
        ApiService.MUSIC_MOST_STREAM_WEEK(),
        ApiService.PLAYLIST_MOST_STREAMED()
      ]);
      return {
        mostStreamedSong: songResponse.result?.[0],
        mostStreamedPlaylist: playlistResponse.result?.[0]
      };
    } catch (error) {
      console.error("Error fetching banner data:", error);
      return {};
    }
  };

  const handleStreamJoined = (streamData) => {
    const newStream = streamData?.livestreamData;
    if (newStream && newStream.channelName) {
      if (!streamRef.current.some((stream) => stream.channelName === newStream.channelName)) {
        const updatedStreams = [newStream, ...streamRef.current];
        setLiveStream(updatedStreams);
        streamRef.current = updatedStreams;
      }
    } else {
      console.error("Invalid stream data:", streamData);
    }
  };
  

  const handleStreamEnded = ({ channelName }) => {
    const updatedStreams = streamRef.current.filter(
      stream => stream.channelName !== channelName
    );
    setLiveStream(updatedStreams);
    streamRef.current = updatedStreams;
  };

  const handleClick = async (channelName) => {
    setLoading(true);
    try {
      const response = await ApiService.PLAYLIST_DETAIL({ _id: channelName, pageNo: 1, pageSize: 1 });
      if (response?.result?.live) {
        dispatch(startStream({ streamData: response.result }));
      }
    } catch (error) {
      notifier.error("Error joining live stream.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ position: "relative", height: "100vh" }}>
<Grid
  container
  sx={{
    height: "100vh", // Full viewport height
    overflow: "hidden", // Prevent content overflow
    position: "relative", // Position relative to allow proper alignment
  }}
>        
<Grid
    item
    xs={12}
    md={10} // Adjusts width to ensure it doesn't overlap the sidebar
    sx={{
      position: "absolute", // Keeps it in place
      left: 0, // Aligns to the left
      right: "17%", // Reserves space for the sidebar
      overflowX: "auto", // Allows horizontal movement
      overflowY: "auto", // Allows vertical scrolling
      height: "100%",
      padding: "0 10px",
      backgroundColor: "black", // Match the existing background
    }}
  >
          <Box sx={{ minHeight: "400px", marginBottom: "20px" }}>
            <TopBan />
          </Box>

          {/* Live Stream Section */}
          {liveStream.length > 0 && (
            <Box sx={{ marginBottom: "20px" }}>
              <FeatureBox
                isStreaming={true}
                title={t("home.tabs.featured.header5", { defaultValue: "Now Live" })}
                route={"/app/feature/NowLive"}
                music={liveStream}
                hideIcons={true}
                isPlaylistNft={true}
                onStream={handleClick}
              />
            </Box>
          )}

          <VideoCarousel studioId={studioId} onVideoSelect={onVideoSelect} isOwner={isOwner} />
          <FeaturedSection />
          <FeaturedArtists />
          <BannerSection bannerData={bannerData} />
        </Grid>

        {/* SidebarRight */}
        <Grid
            item
            xs={12}
            md={3}
            sx={{
              height: "100vh", // Full height
              position: "fixed", // Fix the sidebar
              right: 0, // Align it to the right
              width: "17%", // Sidebar width
              top: 0,
              backgroundColor: "#f5f5f5",
              boxShadow: "-2px 0 5px rgba(0, 0, 0, 0.1)", // Slight shadow for separation
              zIndex: 1, // Ensure it stays above other content
            }}
          >
          <SidebarRight />
        </Grid>
      </Grid>

      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            zIndex: 2000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center"
          }}
        >
          <SIKIload />
        </Box>
      )}
    </Box>
  );
}
